import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import { Controller, useForm } from "react-hook-form";

import {
  ContractShipmentStatus,
  type ListContractShipmentsResponseItem,
  type UpdateContractShipmentStatusRequest
} from "@/interfaces/modules/contract-shipments";

interface ContractShipmentCancellationDialogProps {
  shipment: ListContractShipmentsResponseItem;
  isSaving: boolean;
  onDismiss: () => void;
  onUpdateStatus: (
    contractShipmentId: number,
    data: UpdateContractShipmentStatusRequest,
    messages: Messages
  ) => Promise<boolean>;
}

interface Messages {
  success: string;
  error: string;
}

interface CancellationFormValues {
  notes: string;
}

export function ContractShipmentCancellationDialog({
  shipment,
  isSaving,
  onDismiss,
  onUpdateStatus
}: ContractShipmentCancellationDialogProps): JSX.Element {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm<CancellationFormValues>({
    defaultValues: { notes: "" },
    mode: "onChange"
  });
  const isDisabled = isSaving || isSubmitting;

  async function cancelShipment({ notes }: CancellationFormValues) {
    const wasCanceled = await onUpdateStatus(
      shipment.id,
      {
        status: ContractShipmentStatus.CANCELED,
        notes: notes.trim()
      },
      {
        success: "Envio cancelado com sucesso.",
        error: "Não foi possível cancelar o envio."
      }
    );

    if (wasCanceled) {
      onDismiss();
    }
  }

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Cancelar envio",
        closeButtonAriaLabel: "Fechar"
      }}
      modalProps={{ isBlocking: true }}
      minWidth={420}
    >
      <form onSubmit={event => void handleSubmit(cancelShipment)(event)}>
        <Stack tokens={{ childrenGap: 12 }}>
          <Text>
            Informe o motivo do cancelamento do envio {shipment.code}.
          </Text>

          <Controller
            name="notes"
            control={control}
            rules={{
              validate: value =>
                value.trim().length > 0 || "Informe o motivo do cancelamento."
            }}
            render={({ field, fieldState }) => (
              <TextField
                label="Motivo do cancelamento"
                required
                multiline
                rows={3}
                disabled={isDisabled}
                value={field.value}
                onBlur={field.onBlur}
                onChange={(_, value) => field.onChange(value ?? "")}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <DialogFooter>
          <DefaultButton
            text="Voltar"
            type="button"
            onClick={onDismiss}
            disabled={isDisabled}
          />
          <PrimaryButton
            text="Cancelar envio"
            type="submit"
            disabled={!isValid || isDisabled}
          />
        </DialogFooter>
      </form>
    </Dialog>
  );
}
